import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  PersonalInfoForm,
  PFInfoForm,
  BankInfoForm,
  EducationForm,
  ProfessionalInfoForm,
  FamilyInfoForm,
  WorkingTimeForm,
  DocumentsForm
} from '../components/forms';
import { apiRequest } from '../utils/api';
import suggestionEditBackground from '../assets/images/suggesstionedit.jpg';
import { toTimeInputValue } from '../utils/time';

const createInitialFormState = () => ({
  personal: {
    name: '',
    dob: '',
    gender: '',
    maritalStatus: '',
    fatherName: '',
    spouseName: '',
    phone: '',
    aadhar: '',
    email: '',
    currentAddress: '',
    permanentAddress: '',
    sameAsCurrentAddress: false,
  },
  pf: {
    pfNumber: '',
    uanNumber: '',
    esiNumber: '',
    insuranceNumber: '',
    insurerName: '',
  },
  bank: {
    accountNumber: '',
    reAccountNumber: '',
    ifscCode: '',
    bankName: '',
    panCard: '',
    drivingLicense: '',
  },
  education: {
    hasQualification: false,
    qualifications: [],
  },
  professional: {
    lastCompany: '',
    designation: '',
    fromDate: '',
    toDate: '',
  },
  family: {
    members: [],
  },
  workingTime: {
    type: '',
    shifts: [],
  },
  documents: [],
});

const AddEmployee = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(createInitialFormState);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [submittedData, setSubmittedData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [isLoadingEmployee, setIsLoadingEmployee] = useState(false);
  const [employeeLoadError, setEmployeeLoadError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const isEditing = useMemo(() => Boolean(editingEmployeeId), [editingEmployeeId]);

  const successModalStyles = useMemo(
    () => ({
      wrapper: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundImage: `url(${suggestionEditBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        zIndex: 1080,
      },
      overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
      },
      content: {
        position: 'relative',
        zIndex: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '28px',
        padding: '3rem 2.5rem',
        textAlign: 'center',
        maxWidth: '480px',
        width: '100%',
        boxShadow: '0 24px 64px rgba(15, 23, 42, 0.25)',
        backdropFilter: 'blur(4px)',
      },
      icon: {
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        backgroundColor: 'rgba(46, 204, 113, 0.18)',
        color: '#2ecc71',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '38px',
        margin: '0 auto 1.5rem',
      },
      title: {
        fontSize: '1.75rem',
        fontWeight: 600,
        marginBottom: '0.75rem',
        color: '#1f2937',
      },
      message: {
        fontSize: '1rem',
        color: '#4b5563',
        marginBottom: '2.25rem',
        lineHeight: 1.6,
      },
      actions: {
        display: 'flex',
        gap: '0.75rem',
        flexWrap: 'wrap',
        justifyContent: 'center',
      },
    }),
    [],
  );

  const handleReviewSubmission = () => {
    setShowSuccessModal(false);
  };

  const handleBackToDashboard = () => {
    setShowSuccessModal(false);
    navigate('/dashboard');
  };

  const steps = [
    { title: 'Personal Information', icon: '👤' },
    { title: 'PF Information', icon: '📄' },
    { title: 'Bank Information', icon: '🏦' },
    { title: 'Educational Qualification', icon: '🎓' },
    { title: 'Professional Information', icon: '💼' },
    { title: 'Family', icon: '👨‍👩‍👧‍👦' },
    { title: 'Working Time', icon: '⏰' },
    { title: 'Documents', icon: '📁' }
  ];

  const mapEmployeeToFormData = (employee) => {
    if (!employee) {
      return createInitialFormState();
    }

    const mappedFamily = Array.isArray(employee.family)
      ? employee.family.map((member) => ({
          relation: member.relation ?? '',
          name: member.name ?? '',
          mobile: member.mobile ?? '',
          dob: member.dob ?? '',
          id: member.id ?? `${Date.now()}-${Math.random()}`,
        }))
      : [];

    const mappedDocuments = Array.isArray(employee.documents)
      ? employee.documents.map((document) => ({
          id: document.id ?? `${Date.now()}-${Math.random()}`,
          documentTypeId:
            document.documentTypeId ?? document.document_type_id ?? document.documentName?.id ?? null,
          documentType: document.documentType ?? '',
          documentTypeName: document.documentTypeName ?? document.documentName?.name ?? document.documentType ?? '',
          fileName: document.fileName ?? '',
          filePath: document.filePath ?? '',
        }))
      : [];

    const personalSource =
      typeof employee.personal === 'object' && employee.personal !== null
        ? employee.personal
        : {};
    const pfSource =
      typeof employee.pf === 'object' && employee.pf !== null
        ? employee.pf
        : {};
    const bankSource =
      typeof employee.bank === 'object' && employee.bank !== null
        ? employee.bank
        : {};
    const educationSource =
      typeof employee.education === 'object' && employee.education !== null
        ? employee.education
        : {};
    const professionalSource =
      typeof employee.professional === 'object' && employee.professional !== null
        ? employee.professional
        : {};
    const workingTimeSource =
      typeof employee.workingTime === 'object' && employee.workingTime !== null
        ? employee.workingTime
        : {};

    const mappedWorkingShifts = Array.isArray(workingTimeSource.shifts)
      ? workingTimeSource.shifts.map((shift, index) => ({
          id: shift?.id ?? `shift-${index}`,
          shiftId:
            shift?.shiftId === null || shift?.shiftId === undefined
              ? ''
              : String(shift.shiftId),
          timeFrom: toTimeInputValue(shift?.timeFrom ?? shift?.shiftInTime),
          timeTo: toTimeInputValue(shift?.timeTo ?? shift?.shiftOutTime),
          isPrimary: Boolean(shift?.isPrimary ?? (index === 0)),
          displayOrder: Number.isFinite(Number(shift?.displayOrder))
            ? Number(shift.displayOrder)
            : index,
        }))
      : [];

    return {
      personal: {
        ...personalSource,
        name: employee.name ?? personalSource.name ?? '',
        dob: personalSource.dob ?? '',
        gender: personalSource.gender ?? '',
        maritalStatus: personalSource.maritalStatus ?? '',
        fatherName: personalSource.fatherName ?? '',
        spouseName: personalSource.spouseName ?? '',
        phone: employee.phone ?? personalSource.phone ?? '',
        aadhar: employee.aadhar ?? personalSource.aadhar ?? '',
        email: employee.email ?? personalSource.email ?? '',
        currentAddress: personalSource.currentAddress ?? '',
        permanentAddress: personalSource.permanentAddress ?? '',
        sameAsCurrentAddress: Boolean(personalSource.sameAsCurrentAddress),
        tempId: employee.tempId ?? personalSource.tempId ?? '',
        permId: employee.permId ?? personalSource.permId ?? '',
      },
      pf: {
        ...pfSource,
        pfNumber: pfSource.pfNumber ?? '',
        uanNumber: pfSource.uanNumber ?? '',
        esiNumber: pfSource.esiNumber ?? employee.esiNumber ?? '',
        insuranceNumber: pfSource.insuranceNumber ?? '',
        insurerName: pfSource.insurerName ?? employee.insurerName ?? '',
      },
      bank: {
        ...bankSource,
        accountNumber: bankSource.accountNumber ?? '',
        reAccountNumber: bankSource.accountNumber ?? '',
        ifscCode: bankSource.ifscCode ?? '',
        bankName: bankSource.bankName ?? '',
        panCard: bankSource.panCard ?? '',
        drivingLicense: bankSource.drivingLicense ?? '',
      },
      education: {
        hasQualification: Boolean(
          educationSource.hasQualification ?? (educationSource.qualifications?.length),
        ),
        qualifications: Array.isArray(educationSource.qualifications)
          ? educationSource.qualifications
          : [],
      },
      professional: {
        ...professionalSource,
        lastCompany: professionalSource.lastCompany ?? '',
        designation: professionalSource.designation ?? '',
        fromDate: professionalSource.fromDate ?? '',
        toDate: professionalSource.toDate ?? '',
        salary: professionalSource.salary ?? '',
      },
      family: {
        ...((typeof employee.family === 'object' && employee.family !== null && !Array.isArray(employee.family))
          ? employee.family
          : {}),
        members: mappedFamily,
      },
      workingTime: {
        ...workingTimeSource,
        type: workingTimeSource.type ?? '',
        shifts: mappedWorkingShifts,
      },
      documents: mappedDocuments,
    };
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idParam = params.get('employeeId');

    if (!idParam) {
      setEditingEmployeeId(null);
      setEmployeeLoadError(null);
      setIsLoadingEmployee(false);
      setFormData(createInitialFormState());
      setActiveStep(0);
      return;
    }

    const numericId = Number(idParam);
    if (!numericId || Number.isNaN(numericId)) {
      setEmployeeLoadError('Invalid employee identifier.');
      setEditingEmployeeId(null);
      return;
    }

    let isMounted = true;

    const loadEmployee = async () => {
      try {
        setIsLoadingEmployee(true);
        setEmployeeLoadError(null);
        const response = await apiRequest(
          `/api/admin/employees/${numericId}`,
          {},
          navigate,
        );

        if (!isMounted) {
          return;
        }

        const mapped = mapEmployeeToFormData(response.data);
        setEditingEmployeeId(numericId);
        setFormData(mapped);
        setSubmittedData(null);
        setSubmissionStatus(null);
        setActiveStep(0);
      } catch (error) {
        if (isMounted) {
          setEmployeeLoadError(error.message || 'Failed to load employee details');
        }
      } finally {
        if (isMounted) {
          setIsLoadingEmployee(false);
        }
      }
    };

    loadEmployee();

    return () => {
      isMounted = false;
    };
  }, [location.search, navigate]);

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleFormData = async (stepData) => {
    if (submissionStatus) {
      setSubmissionStatus(null);
      setShowSuccessModal(false);
    }

    const stepKey = Object.keys(stepData).find(key => key !== 'goBack');

    const updatedData = stepKey
      ? {
        ...formData,
        [stepKey]: stepData[stepKey]
      }
      : formData;

    if (stepKey) {
      setFormData(updatedData);
    }

    if (stepData.goBack) {
      handleBack(); // move to previous step
    } else {
      if (activeStep === steps.length - 1) {
        try {
          await handleFinalSubmit(updatedData);
        } catch (_error) {
          // Errors are surfaced via submission status
        }
      } else {
        handleNext(); // move to next step
      }
    }
  };

  const handleFinalSubmit = async (finalData) => {
    const sanitizedData = {
      ...finalData,
      documents: (finalData.documents || []).map(({ file, ...rest }) => rest),
    };

    const requestPayload = {
      ...sanitizedData,
      requestType: isEditing ? 'update' : 'create',
    };

    if (isEditing && editingEmployeeId) {
      requestPayload.employeeId = editingEmployeeId;
    }

    try {
      setIsSubmitting(true);
      setSubmissionStatus(null);
      setShowSuccessModal(false);
      const response = await apiRequest(
        '/api/admin/employee-approvals',
        {
          method: 'POST',
          body: requestPayload,
        },
        navigate,
      );

      setSubmittedData(response.data);
      setSubmissionStatus({
        type: 'success',
        message:
          response.message ||
          (isEditing
            ? 'Employee update request submitted for approval. Review the summary below or continue editing.'
            : 'Employee creation request submitted for approval. Review the summary below or reset the form to add another employee.'),
      });
      setShowSuccessModal(true);
    } catch (error) {
      setSubmittedData(null);
      setSubmissionStatus({
        type: 'error',
        message: error.message || 'Failed to submit employee information.',
      });
      setShowSuccessModal(false);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetForm = () => {
    setFormData(createInitialFormState());
    setActiveStep(0);
    setSubmittedData(null);
    setSubmissionStatus(null);
    setShowSuccessModal(false);
  };

  const renderForm = () => {
    switch (activeStep) {
      case 0:
        return (
          <PersonalInfoForm
            onSubmit={handleFormData}
            data={formData.personal}
            enableValidation
          />
        );
      case 1:
        return (
          <PFInfoForm
            onSubmit={handleFormData}
            data={formData.pf}
            enableValidation
          />
        );
      case 2:
        return (
          <BankInfoForm
            onSubmit={handleFormData}
            data={formData.bank}
            enableValidation
          />
        );
      case 3:
        return (
          <EducationForm
            onSubmit={handleFormData}
            data={formData.education}
            enableValidation
          />
        );
      case 4:
        return (
          <ProfessionalInfoForm
            onSubmit={handleFormData}
            data={formData.professional}
            enableValidation
          />
        );
      case 5:
        return (
          <FamilyInfoForm
            onSubmit={handleFormData}
            data={formData.family}
            enableValidation
          />
        );
      case 6:
        return (
          <WorkingTimeForm
            onSubmit={handleFormData}
            data={formData.workingTime}
            enableValidation={true}
          />
        );
      case 7:
        return (
          <DocumentsForm
            onSubmit={handleFormData}
            data={formData.documents}
            enableValidation={true}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <DashboardLayout title={isEditing ? 'Edit Employee' : 'Add Employee'}>
      {showSuccessModal && submissionStatus?.type === 'success' && (
        <div
          style={successModalStyles.wrapper}
          role="dialog"
          aria-modal="true"
          aria-labelledby="submission-success-heading"
        >
          <div
            style={successModalStyles.overlay}
            onClick={handleReviewSubmission}
            aria-hidden="true"
          />
          <div style={successModalStyles.content}>
            <button
              type="button"
              className="btn-close position-absolute top-0 end-0 m-3"
              aria-label="Close"
              onClick={handleReviewSubmission}
            ></button>
            <div style={successModalStyles.icon} aria-hidden="true">✓</div>
            <h3 id="submission-success-heading" style={successModalStyles.title}>
              Successfully Submitted for Approval!
            </h3>
            <p style={successModalStyles.message}>
              {submissionStatus?.message ||
                (isEditing
                  ? 'Employee update request submitted for approval.'
                  : 'Employee creation request submitted for approval.')}
            </p>
            <div style={successModalStyles.actions}>
              <button
                type="button"
                className="btn btn-dark px-4 py-2 rounded-pill"
                onClick={handleBackToDashboard}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          {submissionStatus?.type === 'error' && (
            <div className="alert alert-danger d-flex justify-content-between align-items-start">
              <div>
                <h6 className="fw-semibold mb-1">Submission Failed</h6>
                <p className="mb-0 small">{submissionStatus.message}</p>
              </div>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setSubmissionStatus(null)}
              ></button>
            </div>
          )}

          {isLoadingEmployee && (
            <div className="alert alert-info" role="status">
              Loading employee details. Please wait...
            </div>
          )}

          {employeeLoadError && (
            <div className="alert alert-danger" role="alert">
              {employeeLoadError}
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center">
              <Link to="/employees" className="btn btn-outline-secondary me-2">
                Back
              </Link>
              <h5 className="mb-0">{isEditing ? 'Edit Employee' : 'Add New Employee'}</h5>
            </div>
          </div>

          <div className="mb-4">
            <div className="d-flex justify-content-between position-relative mb-4">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`step-item ${index <= activeStep ? 'active' : ''}`}
                  style={{ zIndex: 1 }}
                >
                  <div className="step-icon">{step.icon}</div>
                  <div className="step-title">{step.title}</div>
                </div>
              ))}
              <div
                className="progress position-absolute w-100"
                style={{ height: '2px', top: '25%', zIndex: 0 }}
              >
                <div
                  className="progress-bar bg-danger"
                  style={{ width: `${(activeStep / (steps.length - 1)) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {isLoadingEmployee ? (
            <div className="text-center py-5">
              <div className="spinner-border text-danger" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            renderForm()
          )}

          {submittedData && (
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Submission Preview</h6>
                <button className="btn btn-sm btn-outline-danger" onClick={handleResetForm}>
                  Reset Form
                </button>
              </div>
              <div className="bg-light border rounded p-3">
                <pre className="mb-0 small text-wrap" style={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(submittedData, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddEmployee;
